import React, { useEffect, useMemo, useState } from "react";
import { Box, InputAdornment, Stack, TextField, Divider, Button, Collapse, Alert, Chip, Typography, IconButton, Tooltip } from "@mui/material";
import { SubtitleInline } from "components/shared/Typography";
import { Warning, CheckCircle, SmartToy, GpsFixed } from "@mui/icons-material";
import CertificateOverrideModal from "./CertificateCoverageOverrideModal";
import { CERTIFICATE_PROPERTIES } from "constants/insurance_types";
import CollapsibleCard, { CollapsibleCardHeader } from "components/shared/CollapsibleCard";
import { CardContent } from "semantic-ui-react";

// Component to render error text with clickable highlight icon
const ClickableErrorText = ({ errorText, fieldName, onHighlight, hasFieldConfidence }) => {
  if (!errorText) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Typography variant="caption" sx={{ flex: 1, whiteSpace: 'pre-line' }}>
        {errorText}
      </Typography>
      {hasFieldConfidence && fieldName && (
        <Tooltip title="Show on certificate" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onHighlight(fieldName);
            }}
            sx={{
              padding: '2px',
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'error.dark',
              }
            }}
          >
            <GpsFixed fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const isEqualFirstRowToInput = (textFieldInput, validationInput) => {
  return textFieldInput?.split("\n")[0]?.toUpperCase()?.trim() === validationInput?.toUpperCase()?.trim();
};

const isValidAddressDetail = (textFieldInput, validAddress) => {
  const [, ...addressDetails] = textFieldInput?.split("\n") || [];
  return addressDetails.join(',').toUpperCase()?.trim()?.includes(validAddress?.toString()?.toUpperCase()?.trim());
};

const certificateHolderErrorText = ({
  isValidCertificateHolder,
  validCertificateHolder="",
  isValidCertificateHolderAddress1,
  validCertificateHolderAddress1="",
  isValidCertificateHolderCity,
  validCertificateHolderCity="",
  isValidCertificateHolderState,
  validCertificateHolderState="",
  isValidCertificateHolderZip,
  validCertificateHolderZip="",
}) => {
  let errorText = "";
  if (!isValidCertificateHolder) {
    errorText = errorText.concat(`CERTIFICATE HOLDER should match CLIENT "${validCertificateHolder}" \n`);
  }
  if (!isValidCertificateHolderAddress1) {
    errorText = errorText.concat(`CERTIFICATE HOLDER address should match "${validCertificateHolderAddress1}" \n`);
  }
  if (!isValidCertificateHolderCity) {
    errorText = errorText.concat(`CERTIFICATE HOLDER city should match "${validCertificateHolderCity}" \n`);
  }
  if (!isValidCertificateHolderState) {
    errorText = errorText.concat(`CERTIFICATE HOLDER state should match "${validCertificateHolderState}" \n`);
  }
  if (!isValidCertificateHolderZip) {
    errorText = errorText.concat(`CERTIFICATE HOLDER Zip should match "${validCertificateHolderZip}" \n`);
  }
  return errorText;
};

export default function CertificateInfoSection ({ certificateData, handleCertificateInfoUpdate, selectedClient, selectedAgency, vendor, isEdit = true, highlightFieldOnPDF, hasFieldConfidence }) {
  const [numberOfInfoSectionOverrides, setNumberOfInfoSectionOverrides] = useState(0);
  const [displayOverrideSection, setDisplayOverrideSection] = useState(false);
  const [viewInitialDescriptionOfOperation, setViewInitialDescriptionOfOperation] = useState(false);
  
  const validCertificateHolder = selectedAgency?.certificateHolder?.trim() || selectedClient?.name;
  const validCertificateHolderAddress1 = selectedAgency?.certificateHolder?.trim()  ? selectedAgency?.certificateHolderAddress1?.trim() : (selectedClient?.address1 || "");
  const validCertificateHolderCity= selectedAgency?.certificateHolder?.trim()  ? selectedAgency?.certificateHolderCity?.trim() : (selectedClient?.city || "");
  const validCertificateHolderState= selectedAgency?.certificateHolder?.trim()  ? selectedAgency?.certificateHolderState?.trim() : (selectedClient?.state || "");
  const validCertificateHolderZip= selectedAgency?.certificateHolder?.trim()  ? selectedAgency?.certificateHolderZip : (selectedClient?.zip || "");

  const validInsuredName = vendor?.useEntityName ? vendor?.entityName : vendor?.name;
  const isValidInsured = isEqualFirstRowToInput(certificateData?.insured, validInsuredName);
  const isValidCertificateHolder = isEqualFirstRowToInput(certificateData?.certificateHolder, validCertificateHolder);
  const isValidDescriptionOfOperation = certificateData?.initialDescriptionOfOperation ? certificateData?.initialDescriptionOfOperation?.trim() === certificateData?.descriptionOfOperation?.trim() : true;
  const isValidCertificateHolderAddress1 = isValidAddressDetail(certificateData?.certificateHolder,validCertificateHolderAddress1);
  const isValidCertificateHolderCity = isValidAddressDetail(certificateData?.certificateHolder,validCertificateHolderCity);
  const isValidCertificateHolderState =  isValidAddressDetail(certificateData?.certificateHolder,validCertificateHolderState);
  const isValidCertificateHolderZip = isValidAddressDetail(certificateData?.certificateHolder,validCertificateHolderZip);

  function onOverride() {
    setDisplayOverrideSection(true);
  }

  function onCancelOverride() {
    setDisplayOverrideSection(false);
  }

  function onOverrideDetails(updatedOverrides = {}) {
    handleCertificateInfoUpdate({
      target: {
        name: "overrides",
        value: updatedOverrides,
      },
    });
    setDisplayOverrideSection(false);
  }

  
  let overrides = certificateData?.overrides || {};
  if (typeof overrides === "string") {
    // this is a fixup for MNCRT-745 
    // fixup for legacy certificates (dev and stg only)
    // do parsing until we have parsed object
    while (typeof overrides === "string") {
      overrides = JSON.parse(overrides);
    }
  }

  const cerDataOverrides = useMemo(() => {
    const data = {
    ...(!isValidInsured && { [CERTIFICATE_PROPERTIES.insured.label]: false }),
    ...(!isValidCertificateHolder && { [CERTIFICATE_PROPERTIES.certificateHolder.label]: false }),
    ...(!isValidDescriptionOfOperation && { [CERTIFICATE_PROPERTIES.descriptionOfOperation.label]: false }),
    ...overrides,
    }
    if (isValidInsured) {
      delete data[CERTIFICATE_PROPERTIES.insured.label]
    }
    if (isValidCertificateHolder) {
      delete data[CERTIFICATE_PROPERTIES.certificateHolder.label]
    }

    if (isValidDescriptionOfOperation) {
      delete data[CERTIFICATE_PROPERTIES.descriptionOfOperation.label]
    }
  
    return data;
  }, [overrides, isValidCertificateHolder, isValidInsured, isValidDescriptionOfOperation]);

  useEffect(() => {
    const numberOfOverrides = Object.keys(cerDataOverrides).length;
    setNumberOfInfoSectionOverrides(numberOfOverrides);
    if (numberOfOverrides === 0) {
      setDisplayOverrideSection(false);
    }
  }, [cerDataOverrides]);

  const hasInsuredError = !(isValidInsured || cerDataOverrides[CERTIFICATE_PROPERTIES.insured.label]);
  const hasCertificateHolderError = !(
    isValidCertificateHolder ||
    cerDataOverrides[CERTIFICATE_PROPERTIES.certificateHolder.label]
  );
  const hasDescriptionOfOperationError = !(
    isValidDescriptionOfOperation ||
    cerDataOverrides[CERTIFICATE_PROPERTIES.descriptionOfOperation.label]
  );
  const hasCertificateHolderAddressError = !(
    isValidCertificateHolderAddress1 ||
    cerDataOverrides[CERTIFICATE_PROPERTIES.certificateHolder.label]
  );
  const hasCertificateHolderStateCityZipError = !(
    isValidCertificateHolderCity ||
    isValidCertificateHolderState ||
    isValidCertificateHolderZip ||
    cerDataOverrides[CERTIFICATE_PROPERTIES.certificateHolder.label]
  );

  // Display extraction metadata if available
  const extractionMethodLabels = {
    CLAUDE: 'Claude AI',
    OCR: 'Traditional OCR',
    HYBRID: 'Hybrid (Claude + OCR)',
    MANUAL: 'Manual Entry'
  };

  const modelLabels = {
    CLAUDE_3_5_SONNET_20241022: 'Claude 3.5 Sonnet',
    CLAUDE_3_OPUS_20240229: 'Claude 3 Opus',
    CLAUDE_3_HAIKU_20240307: 'Claude 3 Haiku',
    CLAUDE_3_5_HAIKU_20241022: 'Claude 3.5 Haiku'
  };

  return (
    <Stack>
      {/* AI Extraction Metadata Display */}
      {certificateData?.extractionMethod && (
        <Alert
          severity="info"
          icon={<SmartToy />}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2">
              <strong>Extraction Method:</strong> {extractionMethodLabels[certificateData.extractionMethod] || certificateData.extractionMethod}
            </Typography>

            {certificateData.extractionModel && (
              <Typography variant="body2">
                <strong>Model:</strong> {modelLabels[certificateData.extractionModel] || certificateData.extractionModel}
              </Typography>
            )}

            {certificateData.extractionConfidence !== null && certificateData.extractionConfidence !== undefined && (
              <Chip
                icon={<CheckCircle />}
                label={`Confidence: ${(certificateData.extractionConfidence * 100).toFixed(0)}%`}
                color={certificateData.extractionConfidence >= 0.85 ? "success" : "warning"}
                size="small"
              />
            )}

            {certificateData.reprocessedCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                (Reprocessed {certificateData.reprocessedCount} time{certificateData.reprocessedCount > 1 ? 's' : ''})
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Stack>
        <CertificateOverrideModal
          title={"Override Certificate details"}
          overrides={cerDataOverrides}
          open={displayOverrideSection}
          onClose={onCancelOverride}
          onOverride={onOverrideDetails}
        />
      </Stack>
      <Stack >
        {isEdit && !displayOverrideSection && (
          <Button startIcon={<Warning />} sx={{ alignSelf: "end" }} onClick={onOverride} disabled={numberOfInfoSectionOverrides < 1}>
            OVERRIDE
          </Button>
        )}
        {/* <LimitsSection /> */}
      </Stack>
      {certificateData?.additionalInformation && (
        <Stack mb={2}>
          <SubtitleInline>ADDITIONAL INFORMATION</SubtitleInline>
          <TextField
            key={"additionalInformation"}
            name="additionalInformation"
            defaultValue={certificateData.additionalInformation}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            disabled={true}
          />
        </Stack>
      )}
      <Stack direction="row" mt={1} mb={1} spacing={2}>
        <Stack sx={{ flexBasis: "50%", justifyContent: "space-between" }}>
          <Box>
            <SubtitleInline>INSURED</SubtitleInline>
            <TextField
              key={certificateData?.insured ? "insured" : "insuredEmpty"}
              name="insured"
              defaultValue={certificateData?.insured ? String(certificateData.insured).trim() : ""}
              variant="outlined"
              fullWidth
              onChange={handleCertificateInfoUpdate}
              multiline
              rows={4}
              error={hasInsuredError}
              disabled={!isEdit}
              helperText={
                <ClickableErrorText
                  errorText={hasInsuredError ? `INSURED should match SUBCONTRACTOR "${validInsuredName}" ` : null}
                  fieldName="insured"
                  onHighlight={highlightFieldOnPDF}
                  hasFieldConfidence={hasFieldConfidence}
                />
              }
              className={hasInsuredError ? "display-error-line-1" : null}
            />
          </Box>
        </Stack>
        <Stack sx={{ flexBasis: "50%", justifyContent: "space-between" }}>
          <Box>
            <SubtitleInline>CERTIFICATE HOLDER</SubtitleInline>
            <TextField
              key={certificateData?.certificateHolder ? "certificateHolder" : "certificateHolderEmpty"}
              name="certificateHolder"
              defaultValue={certificateData?.certificateHolder || ""}
              variant="outlined"
              fullWidth
              onChange={handleCertificateInfoUpdate}
              multiline
              rows={4}
              error={hasCertificateHolderError || hasCertificateHolderAddressError || hasCertificateHolderStateCityZipError}
              disabled={!isEdit}
              helperText={
                <ClickableErrorText
                  errorText={
                    (hasCertificateHolderError || hasCertificateHolderAddressError || hasCertificateHolderStateCityZipError)
                      ? certificateHolderErrorText({
                          isValidCertificateHolder,
                          validCertificateHolder,
                          isValidCertificateHolderAddress1,
                          validCertificateHolderAddress1,
                          isValidCertificateHolderCity,
                          validCertificateHolderCity,
                          isValidCertificateHolderState,
                          validCertificateHolderState,
                          isValidCertificateHolderZip,
                          validCertificateHolderZip,
                        })
                      : null
                  }
                  fieldName="certificateHolder"
                  onHighlight={highlightFieldOnPDF}
                  hasFieldConfidence={hasFieldConfidence}
                />
              }
              className={`${hasCertificateHolderError ? "display-error-line-1" : null} ${
                hasCertificateHolderAddressError || hasCertificateHolderStateCityZipError ? "display-error-line-234" : null
              }`}
            />
          </Box>
        </Stack>
      </Stack>
      <Stack direction="row" mt={1} mb={2} spacing={2}>
        <Stack sx={{ flexBasis: "50%", justifyContent: "space-between" }}>
          <Box>
            <SubtitleInline>PRODUCER</SubtitleInline>
            <TextField
              key={certificateData?.producer ? "producer" : "producerEmpty"}
              name="producer"
              defaultValue={certificateData?.producer ? String(certificateData.producer).trim() : ""}
              variant="outlined"
              fullWidth
              onBlur={handleCertificateInfoUpdate}
              multiline
              rows={4}
              disabled={!isEdit}
            />
          </Box>
        </Stack>
        <Stack sx={{ flexBasis: "50%" }}>
          <SubtitleInline>PRODUCER CONTACT</SubtitleInline>
          <TextField
            key={certificateData?.producerContactName ? "producerContactName" : "producerContactNameEmpty"}
            InputProps={{
              startAdornment: <InputAdornment position="start">NAME</InputAdornment>,
            }}
            name="producerContactName"
            defaultValue={certificateData?.producerContactName || ""}
            variant="standard"
            fullWidth
            onBlur={handleCertificateInfoUpdate}
            disabled={!isEdit}
          />
          <TextField
            key={certificateData?.producerPhone ? "producerPhone" : "producerPhoneEmpty"}
            InputProps={{
              startAdornment: <InputAdornment position="start">PHONE</InputAdornment>,
            }}
            name="producerPhone"
            defaultValue={certificateData?.producerPhone || ""}
            variant="standard"
            fullWidth
            onBlur={handleCertificateInfoUpdate}
            disabled={!isEdit}
          />
          <TextField
            key={certificateData?.producerFax ? "producerFax" : "producerFaxEmpty"}
            InputProps={{
              startAdornment: <InputAdornment position="start">FAX</InputAdornment>,
            }}
            name="producerFax"
            defaultValue={certificateData?.producerFax || ""}
            variant="standard"
            fullWidth
            onBlur={handleCertificateInfoUpdate}
            disabled={!isEdit}
          />
          <TextField
            key={certificateData?.producerEmail ? "producerEmail" : "producerEmailEmpty"}
            InputProps={{
              startAdornment: <InputAdornment position="start">EMAIL</InputAdornment>,
            }}
            name="producerEmail"
            defaultValue={certificateData?.producerEmail || ""}
            variant="standard"
            fullWidth
            onBlur={handleCertificateInfoUpdate}
            disabled={!isEdit}
          />
        </Stack>
      </Stack>
      <Divider />
      <Stack mt={2} mb={1}>
        <Stack direction="row">
          <SubtitleInline sx={{ flexBasis: "70%" }}>INSURER(S) AFFORDING COVERAGE</SubtitleInline>
          <SubtitleInline sx={{ flexBasis: "30%" }}>NAIC #</SubtitleInline>
        </Stack>

        {["A", "B", "C", "D", "E", "F"].map((insurerLetter) => (
          <Stack direction="row" key={"insurerLetter-" + insurerLetter}>
            <TextField
              key={certificateData?.["insurer" + insurerLetter] ? "insurerLetter" + insurerLetter : "insurerLetterEmpty" + insurerLetter}
              name={"insurer" + insurerLetter}
              defaultValue={certificateData?.["insurer" + insurerLetter] || ""}
              variant="standard"
              fullWidth
              onBlur={handleCertificateInfoUpdate}
              InputProps={{
                startAdornment: <InputAdornment position="start">{`INSURER ${insurerLetter}:`}</InputAdornment>,
              }}
              sx={{ flexBasis: "70%" }}
              disabled={!isEdit}
            />
            <TextField
              key={certificateData?.["insurer" + insurerLetter + "NAIC"] ? "insurer" + insurerLetter + "NAIC" : "insurerEmpty" + insurerLetter + "NAIC"}
              name={"insurer" + insurerLetter + "NAIC"}
              defaultValue={certificateData?.["insurer" + insurerLetter + "NAIC"] || ""}
              variant="standard"
              fullWidth
              onBlur={handleCertificateInfoUpdate}
              sx={{ flexBasis: "30%", ml: 2 }}
              disabled={!isEdit}
            />
          </Stack>
        ))}
      </Stack>
      <Stack mt={1}>
        {certificateData?.initialDescriptionOfOperation && (
          <Stack >
            <CollapsibleCard>
              <CollapsibleCardHeader
                show={viewInitialDescriptionOfOperation}
                sx={{
                  padding: 0,
                  alignItems: "center",
                  ".MuiCardHeader-content .MuiButton-text": {
                    padding: 0,
                  },
                }}
                toggle={() => {
                  setViewInitialDescriptionOfOperation(!viewInitialDescriptionOfOperation);
                }}
                title={
                  <>
                    <Box component="span">REQUESTED DESCRIPTION OF OPERATION</Box>
                  </>
                }
              />
              <CardContent sx={{ padding: 0 }}>
                <Collapse in={viewInitialDescriptionOfOperation}>
                  <Box component="div" width="100%" my={2}>
                    <TextField
                      sx={{
                        mt: 1,
                        "& .MuiInputBase-input.Mui-disabled": {
                          color: "rgba(0,0,0,0.6)",
                          WebkitTextFillColor: "rgba(0,0,0,0.6)",
                        },
                      }}
                      fullWidth
                      multiline
                      minRows={4}
                      name="initialDescriptionOfOperation"
                      defaultValue={certificateData?.initialDescriptionOfOperation}
                      disabled={true}
                    />
                  </Box>
                </Collapse>
              </CardContent>
            </CollapsibleCard>
          </Stack>
        )}
        <SubtitleInline>DESCRIPTION OF OPERATION</SubtitleInline>
        <TextField
          key={certificateData?.descriptionOfOperation ? "descriptionOfOperation" : "descriptionOfOperationEmpty"}
          sx={{ mt: 1 }}
          fullWidth
          multiline
          minRows={4}
          placeholder={"Add description of operation ..."}
          name="descriptionOfOperation"
          defaultValue={certificateData?.descriptionOfOperation}
          onChange={handleCertificateInfoUpdate}
          disabled={!isEdit}
          error={hasDescriptionOfOperationError}
          helperText={
            <ClickableErrorText
              errorText={hasDescriptionOfOperationError ? `DESCRIPTION OF OPERATION SHOULD MATCH REQUESTED DESCRIPTION OF OPERATION ` : null}
              fieldName="descriptionOfOperation"
              onHighlight={highlightFieldOnPDF}
              hasFieldConfidence={hasFieldConfidence}
            />
          }
          className={hasDescriptionOfOperationError ? "coverages-validation-error" : null}
        />
      </Stack>
    </Stack>
  );
}
